import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  InputBase,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import { Close, Send, AutoAwesome, InfoOutlined } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { aiService } from '../services/aiService';
import { useAppTheme } from '../theme';

interface Turn {
  role: 'user' | 'model';
  text: string;
  toolsUsed?: string[];
  error?: boolean;
}

/** Tool name -> what a human would call it. */
const TOOL_LABELS: Record<string, string> = {
  get_financial_summary: 'financial summary',
  list_overdue_invoices: 'overdue invoices',
  get_receivables_ageing: 'receivables ageing',
  list_low_stock_items: 'low stock',
  get_sales_pipeline: 'sales pipeline',
  list_pending_approvals: 'pending approvals',
  get_hr_summary: 'HR summary',
  list_open_tickets: 'open tickets',
  get_project_status: 'project status',
  search_records: 'record search',
  get_inventory_value: 'inventory value',
  get_top_customers: 'top customers',
};

/**
 * The AI assistant.
 *
 * Two things this does that most assistant panels do not:
 *
 *  - It shows which server-side tools produced the answer. The model cannot
 *    query freely — it picks from an allowlist of tenant-scoped, read-only
 *    tools — so naming them turns "trust me" into "here is where this came
 *    from", which is the minimum bar for a number in an accounting product.
 *  - It refuses to exist when unconfigured. `useAssistantAvailable()` gates
 *    the launcher, so a deployment with no GEMINI_API_KEY shows no entry point
 *    rather than a button that always errors.
 */
const AssistantPanel: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { colors } = useAppTheme();
  const [input, setInput] = React.useState('');
  const [turns, setTurns] = React.useState<Turn[]>([]);
  const [conversationId, setConversationId] = React.useState<number | undefined>();
  const endRef = React.useRef<HTMLDivElement>(null);

  const ask = useMutation({
    mutationFn: (prompt: string) => aiService.ask(prompt, conversationId),
    onSuccess: (res) => {
      setConversationId(res.conversationId);
      setTurns((t) => [...t, { role: 'model', text: res.answer, toolsUsed: res.toolsUsed }]);
    },
    onError: (e: any) => {
      setTurns((t) => [
        ...t,
        { role: 'model', text: e?.message || 'The assistant could not answer that.', error: true },
      ]);
    },
  });

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, ask.isPending]);

  const submit = () => {
    const prompt = input.trim();
    if (!prompt || ask.isPending) return;
    setTurns((t) => [...t, { role: 'user', text: prompt }]);
    setInput('');
    ask.mutate(prompt);
  };

  const SUGGESTIONS = [
    'What is overdue right now?',
    'How did revenue look this month?',
    'Which products are low on stock?',
    'What is waiting for my approval?',
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <AutoAwesome sx={{ fontSize: 18, color: colors.primary }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Assistant
        </Typography>
        <Tooltip title="Answers come only from your workspace data, via a fixed set of read-only tools. The assistant cannot create or change anything.">
          <InfoOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {turns.length === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask about your workspace. It reads live data and will say so when it cannot see something.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {SUGGESTIONS.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  variant="outlined"
                  onClick={() => {
                    setTurns([{ role: 'user', text: s }]);
                    ask.mutate(s);
                  }}
                  sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.875, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                />
              ))}
            </Box>
          </Box>
        )}

        {turns.map((turn, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Box
              sx={{
                px: 1.75,
                py: 1.25,
                borderRadius: 2,
                backgroundColor:
                  turn.role === 'user'
                    ? `${colors.primary}14`
                    : turn.error
                    ? 'error.light'
                    : 'action.hover',
                ...(turn.error && { color: 'error.contrastText' }),
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {turn.text}
              </Typography>
            </Box>

            {/* Provenance. An unattributed number is not actionable. */}
            {turn.toolsUsed && turn.toolsUsed.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.875, pl: 0.5 }}>
                <Typography variant="caption" color="text.disabled">
                  based on
                </Typography>
                {Array.from(new Set(turn.toolsUsed)).map((t) => (
                  <Chip
                    key={t}
                    label={TOOL_LABELS[t] || t}
                    size="small"
                    sx={{ height: 18, fontSize: '0.625rem' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ))}

        {ask.isPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
            <CircularProgress size={13} />
            <Typography variant="caption" color="text.secondary">
              Reading your data…
            </Typography>
          </Box>
        )}
        <div ref={endRef} />
      </Box>

      <Divider />
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1.5 }}>
        <InputBase
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask something…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            fontSize: '0.875rem',
          }}
        />
        <IconButton
          color="primary"
          onClick={submit}
          disabled={!input.trim() || ask.isPending}
          sx={{ mb: 0.25 }}
        >
          <Send fontSize="small" />
        </IconButton>
      </Box>
    </Drawer>
  );
};

/**
 * Whether to offer the assistant at all.
 *
 * Insights do NOT depend on this — they are rule-computed server-side and work
 * with no credentials.
 */
export const useAssistantAvailable = () => {
  const { data } = useQuery<{ configured: boolean; model: string | null; capabilities: string[] }>({
    queryKey: ['ai-status'],
    queryFn: () => aiService.status(),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return Boolean(data?.configured);
};

export default AssistantPanel;
